'use client'

import React, { useEffect, useRef } from 'react'

import styled from 'styled-components'
import {
  CloseOutlined, FileOutlined, ReloadOutlined,
  CloseCircleOutlined, CheckCircleOutlined, StopOutlined,
  Button, Card, Space, Typography, List,
  Progress, Avatar, Tooltip, theme
} from 'shared/ui'
import { useAppDispatch } from "shared/config/store"
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { useLocation, useNavigate } from 'react-router-dom'


import {
  closeWidgetThunk,
  removeFileFromUploadingThunk,
  restartFileUploadingThunk,
  UPLOADER_LIST_TYPES,
  UPLOADING_STATUSES
} from '../model/slice/filesUploadingSlice'
import {
  getCurrentUploadingTypeSelector,
  getFilesUploadingStatusSelector,
  getFilesWithUnsupportedMediaTypeCountSelector,
  getIsSizeQuotaExceedErrorSelector,
  getUploadingFilesSelector,
  uploadingFilesState
} from '../selectors/filesUploadingSelectors'

const TYPES_FOR_PUBLIC_LINK_OWNER_SIZE_QUOTA_EXCEED_ERROR = [
  UPLOADER_LIST_TYPES.SHARED_ALBUM,
  UPLOADER_LIST_TYPES.FAMILY_TIMELINE_COLLABORATOR,
  UPLOADER_LIST_TYPES.FAMILY_ALBUM_COLLABORATOR,
  UPLOADER_LIST_TYPES.SHARED_WITH_ME_FILES
]

const { Text, Title } = Typography

// Define the interface based on what's expected by the thunk
interface IUploadingFile {
  id: string;
  name: string;
  size: number;
  type: string;
  state: any;
}

// Using the actual type from the selectors
type IExtendedUploadingFile = ReturnType<typeof getUploadingFilesSelector>[0];

function hasPublicLinkOwnerSizeQuotaExceedError(item: IExtendedUploadingFile): boolean {
  if (item.state) {
    const { state } = item
    const { error } = state

    if (error?.response &&
      item.uploadingSourceType &&
      TYPES_FOR_PUBLIC_LINK_OWNER_SIZE_QUOTA_EXCEED_ERROR
        .includes(item.uploadingSourceType)) {
      const status = error.response?.status

      return status === 402
    }
  }

  return false
}

const { useToken } = theme

const ConnectedFilesUploadingWidget = React.memo(() => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const files = getUploadingFilesSelector()
  const status = getFilesUploadingStatusSelector()
  const listState = uploadingFilesState()
  const isSizeQuotaExceedError = getIsSizeQuotaExceedErrorSelector()
  const unsupportedFilesCount = getFilesWithUnsupportedMediaTypeCountSelector()
  const currentUploadingType = getCurrentUploadingTypeSelector()

  const sizeQuotaExceedNotification = useRef<string | null>(null)
  const publicLinkOwnerSizeQuotaExceedNotification = useRef<string | null>(null)
  const unsupportedFilesCountRef = useRef(0)
  const currentUploadingTypeRef = useRef(currentUploadingType)
  const filesWithPublicLinkOwnerSizeQuotaExceedErrorLengthRef = useRef(0)
  const { token }  = useToken()
  const greenColor = token.green
  const redColor = token.red

  useEffect(() => {
    currentUploadingTypeRef.current = currentUploadingType
  }, [currentUploadingType])

  useEffect(() => {
    const listener = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      return event.returnValue = 'l_common_exitPage'
    }

    if (listState.isUploading) {
      window.addEventListener('beforeunload', listener)
    }

    return () => {
      window.removeEventListener('beforeunload', listener)
    }
  }, [listState])

  useEffect(() => {
    if (isSizeQuotaExceedError) {
      const notificationOptions = {
        isPermanent: true,
        type: NOTIFICATION_TYPES.WARNING,
        title: 'You are out of space',
        message: "Can't upload photo",
        typeError: 'SizeQuotaExceeded',
        callback: () => {
          // hideNotification(sizeQuotaExceedNotification.current)
          sizeQuotaExceedNotification.current = null
        }
      }

      showNotification(notificationOptions)
      // We can't assign the result of showNotification to the ref since it returns void
      // Just set it to a non-null value to indicate a notification was shown
      sizeQuotaExceedNotification.current = 'notification-shown'
    }
  }, [isSizeQuotaExceedError])

  useEffect(() => {
    if (!files || files.some(file => !file.uploadId)) {
      return
    }

    const filesWithPublicLinkOwnerSizeQuotaExceedError = files.filter(hasPublicLinkOwnerSizeQuotaExceedError)

    if (filesWithPublicLinkOwnerSizeQuotaExceedError.length < filesWithPublicLinkOwnerSizeQuotaExceedErrorLengthRef.current) {
      filesWithPublicLinkOwnerSizeQuotaExceedErrorLengthRef.current = filesWithPublicLinkOwnerSizeQuotaExceedError.length
    }

    if (filesWithPublicLinkOwnerSizeQuotaExceedErrorLengthRef.current < filesWithPublicLinkOwnerSizeQuotaExceedError.length && !publicLinkOwnerSizeQuotaExceedNotification.current &&
      currentUploadingTypeRef.current &&
      TYPES_FOR_PUBLIC_LINK_OWNER_SIZE_QUOTA_EXCEED_ERROR.includes(currentUploadingTypeRef.current)
    ) {
      const notificationOptions = {
        withHiddenArrow: true,
        type: NOTIFICATION_TYPES.WARNING,
        title: 'The owner of the cloud has run out of storage space',
        message: "Can't upload to the cloud",
        typeError: 'SizeQuotaExceededOwner',
        isCallbackAfterHide: true,
        callback: () => {
          publicLinkOwnerSizeQuotaExceedNotification.current = null
        }
      }

      showNotification(notificationOptions)
      // We can't assign the result of showNotification to the ref since it returns void
      // Just set it to a non-null value to indicate a notification was shown
      publicLinkOwnerSizeQuotaExceedNotification.current = 'notification-shown'
      filesWithPublicLinkOwnerSizeQuotaExceedErrorLengthRef.current = filesWithPublicLinkOwnerSizeQuotaExceedError.length
    }
  }, [files])

  useEffect(() => {
    if (unsupportedFilesCount > unsupportedFilesCountRef.current) {
      unsupportedFilesCountRef.current = unsupportedFilesCount

      // showNotification({
      //   type: NOTIFICATION_TYPES.WARNING,
      //   title: t('l_notification_invalidContent'),
      // })
    }
  }, [unsupportedFilesCount])

  const handleCancelFileUploading = (file: IExtendedUploadingFile) => {
    // Convert to IUploadingFile before passing to the thunk
    const uploadingFile: IUploadingFile = {
      id: file.id,
      name: file.name || '',
      size: typeof file.size === 'string' ? parseInt(file.size, 10) || 0 : 0,
      type: file.type,
      state: file.state
    }
    dispatch(removeFileFromUploadingThunk(uploadingFile))
  }

  const handleClose = () => {
    dispatch(closeWidgetThunk())
  }

  const handleReloadFileUploading = (item: IExtendedUploadingFile) => {
    dispatch(restartFileUploadingThunk(item))
  }

  const handleReloadAllReloadableFiles = () => {
    const itemsForReload = files.filter((file) => file.state.isError && file.state?.error?.response?.status !== 415)

    itemsForReload.forEach((item) => {
      dispatch(restartFileUploadingThunk(item))
    })
  }

  const navigateToParentBoard = (parentId: string) => {
    navigate(`/dashboard/board/${parentId}`)
  }

  if (currentUploadingType === UPLOADER_LIST_TYPES.AI_ASSISTANT_ATTACHMENTS) {
    return null
  }

  if (status === UPLOADING_STATUSES.IDLE) {
    return null
  }

  // Calculate overall progress
  const totalProgress = files.length
    ? Math.round(files.reduce((sum, file) => sum + file.state.progress, 0) / files.length)
    : 0

  const hasErrors = files.some(file => file.state.isError)
  const fileCountText = `${files.length} ${files.length === 1 ? 'file' : 'files'}`

  const isItemCanBeReuploaded = (itemError: any) => {
    if(!itemError) {
      const errorStatus = itemError?.status
      return errorStatus && errorStatus !== 422
    }
    return false
  }

  const handleCardClick = (item: IExtendedUploadingFile) => {
    item.parentId && navigateToParentBoard(item.parentId)
  }


  const getItemActions = (item: IExtendedUploadingFile) => {
    const progress = item.state.progress
    const isError = item.state.isError
    const itemError = item.state?.error?.response
    const iconStyles = { fontSize: 24 }

    return [
      isError ? isItemCanBeReuploaded(itemError) ? (
        <Tooltip title="Retry">
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined style={iconStyles} />}
            onClick={() => handleReloadFileUploading(item)}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Error">
          <Button
            type="text"
            size="middle"
            icon={<StopOutlined style={iconStyles} />}
            disabled
          />
        </Tooltip>
      ) : progress < 100 ? (
        <Tooltip title="Cancel">
          <Button
            type="text"
            size="middle"
            icon={<CloseCircleOutlined style={{ ...iconStyles, color: redColor }} />}
            onClick={() => handleCancelFileUploading(item)}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Done">
          <Button
            type="text"
            size="middle"
            icon={<CheckCircleOutlined style={{ ...iconStyles, color: greenColor }} />}
            disabled
          />
        </Tooltip>
      )
    ]
  }

  return (
    <StyledWidget>
      <Card
        title={(
          <Space align="center">
            <Progress
              type="circle"
              percent={totalProgress}
              size={40}
              status={hasErrors ? "exception" : totalProgress === 100 ? "success" : "active"}
              strokeWidth={10}
            />

            <Space direction="vertical"
              size={0}
            >
              <Title level={5}
                style={{ margin: 0 }}
              >
                Files Uploading
              </Title>

              <Text type="secondary"
                style={{ fontSize: '12px' }}
              >
                {fileCountText}

                {' '}

•
                {totalProgress}
% complete
              </Text>
            </Space>
          </Space>
        )}
        extra={(
          <Space>
            {hasErrors && (
              <Tooltip title="Retry failed uploads">
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={handleReloadAllReloadableFiles}
                />
              </Tooltip>
            )}

            <Tooltip title="Close">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
              />
            </Tooltip>
          </Space>
        )}
        bodyStyle={{ padding: '8px 12px', maxHeight: '300px', overflow: 'auto' }}
      >
        <Space direction="vertical"
          style={{ width: '100%' }}
        >
          <List
            itemLayout="horizontal"
            dataSource={files}
            renderItem={(item) => {
              const isError = item.state.isError
              const progress = item.state.progress

              return (
                <List.Item
                  key={item.id}
                  actions={getItemActions(item)}
                >
                  <SItemContainer onClick={() => handleCardClick(item)}>
                    <List.Item.Meta
                      avatar={<Avatar icon={<FileOutlined />} />}
                      title={(
                        <Tooltip title={item.name}>
                          <Text ellipsis
                            style={{ maxWidth: 180 }}
                          >
                            {item.name || 'Unnamed file'}
                          </Text>
                        </Tooltip>
                      )}
                      description={(
                        <Space direction="vertical"
                          style={{ width: '100%' }}
                        >
                          <Text type="secondary"
                            style={{ fontSize: '12px' }}
                          >
                            {item.size || ''}
                          </Text>

                          <Progress
                            percent={progress}
                            size="small"
                            status={isError ? "exception" : progress === 100 ? "success" : "active"}
                            showInfo={false}
                          />
                        </Space>
                      )}
                    />
                  </SItemContainer>

                </List.Item>
              )
            }}
          />
        </Space>
      </Card>
    </StyledWidget>
  )
})

const SItemContainer = styled.div`
  width: 100%;
  &:hover {
    cursor: pointer;
  }
`

const StyledWidget = styled.div`
  position: fixed;
  width: 350px;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  background-color: #fff;
  
  .ant-card {
    border-radius: 8px;
    overflow: hidden;
  }
  
  .ant-card-head {
    padding: 0 16px;
    min-height: 48px;
  }
  
  .ant-list-item {
    padding: 8px 0;
  }
  
  .ant-progress {
    margin-bottom: 0;
  }
  
  .ant-progress-circle .ant-progress-text {
    font-size: 12px;
  }
`

export { ConnectedFilesUploadingWidget as FilesUploadingWidget }
